
import React, { useState, useEffect } from 'react';
import { getPaymentHistory } from '../services/stripeApi';
import { ShoppingBag, Calendar, CreditCard, CheckCircle, Clock, FileText, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function OrderHistory({ userId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const data = await getPaymentHistory(userId);
        setOrders(data);
      } catch (error) {
        console.error('Erreur chargement commandes:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) fetchOrders();
  }, [userId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: { bg: '#f0fff4', color: '#38a169', icon: <CheckCircle size={14} />, text: 'Payé' },
      pending: { bg: '#fffaf0', color: '#dd6b20', icon: <Clock size={14} />, text: 'En attente' },
      failed: { bg: '#fff5f5', color: '#e53e3e', text: 'Échoué' }
    };

    const config = styles[status] || { bg: '#edf2f7', color: '#4a5568', text: status };

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        padding: '4px 10px',
        borderRadius: '20px',
        backgroundColor: config.bg,
        color: config.color,
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {config.icon}
        {config.text}
      </div>
    );
  };

  const generateInvoice = (order) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(22);
    doc.setTextColor(44, 82, 130);
    doc.text('FACTURE', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('SAVATEVENT', 20, 40);
    doc.text('Service Organisation de Compétition', 20, 46);
    doc.text('https://savatevent.com', 20, 52);
    
    // Facture Info
    doc.text(`Facture n°: ${order.id.substring(0, 8).toUpperCase()}`, 140, 40);
    doc.text(`Date: ${formatDate(order.created_at).split(' ')[0]}`, 140, 46);
    doc.text(`Statut: ${order.status === 'completed' ? 'PAYÉE' : 'EN ATTENTE'}`, 140, 52);
    
    // Client Info
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('DESTINATAIRE:', 20, 70);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.text(`Client ID: ${userId}`, 20, 78);
    
    // Table
    const tableData = [
      [
        order.description || 'Achat de crédits',
        `${order.credits} crédits`,
        '1',
        `${(order.amount / 100).toFixed(2)}€`
      ]
    ];
    
    autoTable(doc, {
      startY: 90,
      head: [['Description', 'Quantité', 'Unités', 'Prix TTC']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [44, 82, 130] }
    });
    
    // Total
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`TOTAL TTC: ${(order.amount / 100).toFixed(2)}€`, 140, finalY + 10);
    
    // Footer
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text('Merci de votre confiance pour SavatEvent.', 105, 280, { align: 'center' });
    
    doc.save(`Facture-SavatEvent-${order.id.substring(0, 8)}.pdf`);
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}>Chargement de l'historique...</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ShoppingBag size={24} color="#3182ce" />
          Mes Commandes
        </h2>
        <p style={{ color: '#718096' }}>Historique de vos achats de crédits</p>
      </div>

      {orders.length === 0 ? (
        <div style={{ 
          backgroundColor: 'white', padding: '40px', borderRadius: '12px', 
          textAlign: 'center', border: '1px solid #e2e8f0' 
        }}>
          <CreditCard size={48} color="#cbd5e0" style={{ marginBottom: '16px' }} />
          <p style={{ color: '#718096' }}>Vous n'avez pas encore passé de commande.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {orders.map((order) => (
            <div key={order.id} style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>
                  {order.description || 'Achat de crédits'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', color: '#718096', fontSize: '13px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={14} /> {formatDate(order.created_at)}
                  </span>
                  <span style={{ fontWeight: '600', color: '#4a5568' }}>
                    + {order.credits} crédits
                  </span>
                </div>
              </div>
              <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#2d3748' }}>
                  {(order.amount / 100).toFixed(2)}€
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {order.status === 'completed' && (
                        <button 
                            onClick={() => generateInvoice(order)}
                            title="Télécharger la facture"
                            style={{
                                background: 'none', border: '1px solid #cbd5e0', 
                                borderRadius: '6px', padding: '4px 8px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '4px',
                                color: '#4a5568', fontSize: '12px'
                            }}
                        >
                            <Download size={14} /> PDF
                        </button>
                    )}
                    {getStatusBadge(order.status)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
